import { Link, useParams } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { Badge, PageTitle, card, linkCls } from "@/components/admin/ui";
import { money } from "@/lib/format";

const tone = (s) => ({ paid: "green", pending: "yellow", refunded: "blue", failed: "red", expired: "gray" }[s] || "gray");

const Stat = ({ label, value, testId }) => (
  <div className={`${card} p-4`}>
    <p className="text-[12px] text-[#6B7280]">{label}</p>
    <p className="text-[22px] font-semibold" data-testid={testId}>{value}</p>
  </div>
);

export default function AdminCustomerDetail() {
  const { email } = useParams();
  const { data, error } = useApi(`/admin/customers/${encodeURIComponent(email)}`);

  if (error) return <p className="text-[14px] text-[#6B7280]" data-testid="customer-not-found">Customer not found.</p>;
  if (!data) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  return (
    <div data-testid="admin-customer-detail-page">
      <Link to="/admin/customers" className={`text-[13px] ${linkCls}`} data-testid="back-to-customers">
        ← All customers
      </Link>
      <div className="mt-3">
        <PageTitle title={data.email} />
      </div>
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Passes bought" value={data.purchases.filter((p) => p.payment_status === "paid").length} testId="customer-pass-count" />
        <Stat label="Total spent" value={money(data.total_spent, data.currency)} testId="customer-total-spent" />
        <Stat label={data.is_admin ? `Organizer earned (after ${data.platform_fee_pct}% fee)` : `You earned (after ${data.platform_fee_pct}% fee)`} value={money(data.total_earnings, data.currency)} testId="customer-total-earnings" />
      </div>
      <div className={`${card} overflow-hidden`} data-testid="customer-purchases-table">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-[13px]">
          <thead className="bg-[#F9FAFB] text-left text-[12px] text-[#6B7280]">
            <tr>
              <th className="px-4 py-2.5 font-medium">Date</th>
              <th className="px-4 py-2.5 font-medium">Festival</th>
              <th className="px-4 py-2.5 font-medium">Pass</th>
              <th className="px-4 py-2.5 font-medium">Bought from</th>
              <th className="px-4 py-2.5 font-medium text-right">Price</th>
              <th className="px-4 py-2.5 font-medium text-right">Fee</th>
              <th className="px-4 py-2.5 font-medium text-right">{data.is_admin ? "Organizer earned" : "You earned"}</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {data.purchases.map((p) => (
              <tr key={p.session_id} data-testid="customer-purchase-row">
                <td className="px-4 py-2.5 whitespace-nowrap text-[#6B7280]">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 font-medium">{p.festival_name}</td>
                <td className="px-4 py-2.5">{p.tier_name}</td>
                <td className="px-4 py-2.5 text-[#6B7280]" data-testid="customer-purchase-source">
                  {p.video_id ? (
                    <Link to={`/watch/${p.video_id}`} target="_blank" className={linkCls}>
                      {p.video_title || "Video"}
                    </Link>
                  ) : (
                    "Festival page"
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">{money(p.amount_total, p.currency)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-right text-[#6B7280]">−{money(p.platform_fee, p.currency)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap text-right font-semibold">{money(p.organizer_earnings, p.currency)}</td>
                <td className="px-4 py-2.5">
                  <Badge tone={tone(p.payment_status)}>{p.payment_status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
